'use client'

import { useEffect, useState } from 'react'

/**
 * Braille-grid snake spinner. Reimplementation of the `snake` generator from
 * github.com/gunnargray-dev/unicode-animations — a snake of 4 dots travels a
 * boustrophedon path across a 4x4 grid, rendered as two braille characters.
 */

const BRAILLE_DOT_MAP = [
  [0x01, 0x08],
  [0x02, 0x10],
  [0x04, 0x20],
  [0x40, 0x80],
]

function gridToBraille(grid: boolean[][]): string {
  const rows = grid.length
  const cols = grid[0]?.length ?? 0
  const charCount = Math.ceil(cols / 2)
  let out = ''
  for (let c = 0; c < charCount; c += 1) {
    let code = 0x2800
    for (let r = 0; r < 4 && r < rows; r += 1) {
      for (let d = 0; d < 2; d += 1) {
        const col = c * 2 + d
        if (col < cols && grid[r]?.[col]) code |= BRAILLE_DOT_MAP[r][d]
      }
    }
    out += String.fromCodePoint(code)
  }
  return out
}

function generateSnakeFrames(): string[] {
  const W = 4
  const H = 4
  const path: Array<[number, number]> = []
  for (let r = 0; r < H; r += 1) {
    if (r % 2 === 0) {
      for (let c = 0; c < W; c += 1) path.push([r, c])
    } else {
      for (let c = W - 1; c >= 0; c -= 1) path.push([r, c])
    }
  }
  const frames: string[] = []
  for (let i = 0; i < path.length; i += 1) {
    const grid = Array.from({ length: H }, () => Array<boolean>(W).fill(false))
    for (let t = 0; t < 4; t += 1) {
      const idx = (i - t + path.length) % path.length
      const [row, col] = path[idx]
      grid[row][col] = true
    }
    frames.push(gridToBraille(grid))
  }
  return frames
}

const FRAMES = generateSnakeFrames()

export interface SnakeSpinnerProps {
  intervalMs?: number
  fontSize?: number
  color?: string
  letterSpacing?: number
}

export function SnakeSpinner({
  intervalMs = 80,
  fontSize = 48,
  color = 'var(--sb-color-primary-default)',
  letterSpacing = -2,
}: SnakeSpinnerProps) {
  const [frame, setFrame] = useState(0)

  useEffect(() => {
    const id = window.setInterval(() => {
      setFrame((f) => (f + 1) % FRAMES.length)
    }, intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])

  return (
    <span
      aria-hidden="true"
      style={{
        fontFamily: 'ui-monospace, "SF Mono", SFMono-Regular, Menlo, Consolas, monospace',
        fontSize,
        lineHeight: 1,
        color,
        letterSpacing,
        fontWeight: 400,
        display: 'inline-block',
        textShadow: `0 0 24px ${withAlpha(color, 0.25)}`,
        userSelect: 'none',
      }}
    >
      {FRAMES[frame]}
    </span>
  )
}

function withAlpha(color: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(color)
  // Non-hex colors (design-token var() references) tint via color-mix so the
  // glow honors the alpha instead of silently rendering at full strength.
  if (!m) return `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`
  const n = parseInt(m[1], 16)
  const r = (n >> 16) & 0xff
  const g = (n >> 8) & 0xff
  const b = n & 0xff
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
